<template>
  <div class="diary-page">
    <div class="page-head">
      <h1 class="page-title">日记</h1>
      <button @click="openAddModal" class="btn-primary">+ 写日记</button>
    </div>

    <div v-if="loading" class="state-msg">加载中...</div>
    <div v-else-if="diaries.length === 0" class="state-msg empty">开始记录你的第一篇日记吧</div>
    
    <template v-else>
      <div class="diary-grid">
        <div
          v-for="diary in diaries"
          :key="diary.id"
          class="diary-card"
          @click="viewDiary(diary)"
        >
        <div class="diary-card-header">
          <h3 class="diary-title">{{ diary.title || '无标题' }}</h3>
          <div class="diary-meta">
            <span class="date-tag">{{ formatDateShort(diary.createTime) }}</span>
            <span v-if="diary.mood" class="mood-tag">{{ diary.mood }}</span>
            <span v-if="diary.weather" class="weather-tag">{{ diary.weather }}</span>
          </div>
        </div>
        <p class="diary-preview">
          {{ truncateText(diary.content, 100) }}
        </p>
        <div class="card-actions" @click.stop>
          <button @click="editDiary(diary)" class="btn-icon">✏️</button>
          <button @click="deleteDiary(diary)" class="btn-icon delete">🗑️</button>
        </div>
      </div>
    </div>
    <div class="pagination-wrap">
        <button class="pagination-btn" :disabled="diaryPageNo <= 1" @click="goDiaryPage(diaryPageNo - 1)">上一页</button>
        <span class="pagination-info">第 {{ diaryPageNo }} / {{ totalDiaryPages }} 页，共 {{ totalDiaries }} 条</span>
        <button class="pagination-btn" :disabled="diaryPageNo >= totalDiaryPages" @click="goDiaryPage(diaryPageNo + 1)">下一页</button>
      </div>
    </template>

    <!-- 写日记/查看日记弹窗 -->
    <div v-if="showModal" class="modal full-screen-modal">
      <div class="modal-content diary-modal">
        <div class="modal-header">
          <div class="header-toolbar">
            <div class="header-left">
              <button @click="closeModal" class="btn-back">← 返回</button>
            </div>
            <div class="header-right" v-if="!isViewMode">
              <select v-model="currentDiary.mood" class="select-input">
                <option value="">心情</option>
                <option value="开心">😊 开心</option>
                <option value="难过">😔 难过</option>
                <option value="生气">😠 生气</option>
                <option value="平静">😐 平静</option>
              </select>
              <select v-model="currentDiary.weather" class="select-input">
                <option value="">天气</option>
                <option value="晴">☀️ 晴</option>
                <option value="雨">🌧️ 雨</option>
                <option value="阴">☁️ 阴</option>
                <option value="雪">❄️ 雪</option>
              </select>
              <button @click="saveDiary" class="btn-save" :disabled="saving">{{ saving ? '保存中...' : '保存' }}</button>
            </div>
            <div class="header-right" v-else>
               <button @click="switchToEdit" class="btn-edit">编辑</button>
            </div>
          </div>
          <div class="header-title-row">
            <input 
              v-model="currentDiary.title" 
              type="text" 
              class="title-input" 
              placeholder="写下今天的标题..."
              :readonly="isViewMode"
            >
          </div>
        </div>
        <div class="modal-body diary-body">
           <div v-if="isViewMode" class="diary-view-content">
             <div class="view-meta">
               <span>{{ formatDate(currentDiary.createTime) }}</span>
               <span v-if="currentDiary.mood"> | 心情: {{ currentDiary.mood }}</span>
               <span v-if="currentDiary.weather"> | 天气: {{ currentDiary.weather }}</span>
             </div>
             <div class="view-text">{{ currentDiary.content }}</div>
           </div>
           <textarea 
             v-else
             v-model="currentDiary.content" 
             class="content-editor" 
             placeholder="今天发生了什么..."
           ></textarea>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDiaries, addDiary, updateDiary, deleteDiary } from '@/api/diary'

export default {
  name: 'DiaryPage',
  data() {
    return {
      userName: localStorage.getItem('userName'),
      diaries: [],
      loading: false,
      diaryPageNo: 1,
      diaryPageSize: 20,
      totalDiaries: 0,
      showModal: false,
      isViewMode: false,
      saving: false,
      currentDiary: {
        id: null,
        title: '',
        content: '',
        mood: '',
        weather: '',
        createTime: ''
      }
    }
  },
  computed: {
    totalDiaryPages() {
      if (this.totalDiaries <= 0 || this.diaryPageSize <= 0) return 1
      return Math.ceil(this.totalDiaries / this.diaryPageSize) || 1
    }
  },
  mounted() {
    this.fetchDiaries()
  },
  methods: {
    goDiaryPage(page) {
      if (page < 1 || page > this.totalDiaryPages) return
      this.diaryPageNo = page
      this.fetchDiaries()
    },
    async fetchDiaries() {
      this.loading = true
      try {
        const res = await getDiaries(this.userName, this.diaryPageNo, this.diaryPageSize)
        if (res.data.success && res.data.data) {
          const raw = res.data.data
          this.diaries = raw.items || []
          this.totalDiaries = Number(raw.total) || 0
        } else {
          this.diaries = []
          this.totalDiaries = 0
        }
      } catch (error) {
        console.error('获取日记失败', error)
        this.diaries = []
        this.totalDiaries = 0
      } finally {
        this.loading = false
      }
    },
    openAddModal() {
      this.currentDiary = {
        title: '',
        content: '',
        mood: '',
        weather: ''
      }
      this.isViewMode = false
      this.showModal = true
    },
    viewDiary(diary) {
      this.currentDiary = { ...diary }
      this.isViewMode = true
      this.showModal = true
    },
    editDiary(diary) {
      this.currentDiary = { ...diary }
      this.isViewMode = false
      this.showModal = true
    },
    switchToEdit() {
      this.isViewMode = false
    },
    async saveDiary() {
      if (this.saving) return
      if (!this.currentDiary.content) {
        this.$message.warning('日记内容不能为空')
        return
      }
      
      this.saving = true
      try {
        if (this.currentDiary.id) {
          // 更新
          const updateData = {
            title: this.currentDiary.title,
            content: this.currentDiary.content,
            mood: this.currentDiary.mood,
            weather: this.currentDiary.weather
          }
          const res = await updateDiary(this.currentDiary.id, this.userName, updateData)
          if (res.data.success) {
            this.fetchDiaries()
            this.$message.success('更新成功')
            this.closeModal()
          } else {
            this.$message.error('更新失败: ' + res.data.errMsg)
          }
        } else {
          // 添加
          const addData = {
            title: this.currentDiary.title,
            content: this.currentDiary.content,
            mood: this.currentDiary.mood,
            weather: this.currentDiary.weather
          }
          const res = await addDiary(this.userName, addData)
          if (res.data.success) {
            this.fetchDiaries()
            this.closeModal()
          } else {
            this.$message.error('发布失败: ' + res.data.errMsg)
          }
        }
      } catch (error) {
        console.error('保存失败', error)
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },
    async deleteDiary(diary) {
      if (!confirm('确定删除这篇日记吗？')) return
      try {
        const res = await deleteDiary(diary.id)
        if (res.data.success) {
          if (this.diaries.length === 1 && this.diaryPageNo > 1) this.diaryPageNo--
          this.fetchDiaries()
        }
      } catch (error) {
        console.error('删除失败', error)
      }
    },
    closeModal() {
      this.showModal = false
      this.isViewMode = false
    },
    
    // 日期格式化辅助函数
    getDay(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).getDate()
    },
    getMonth(dateStr) {
      if (!dateStr) return ''
      const months = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
      return months[new Date(dateStr).getMonth()]
    },
    getTime(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
    },
    formatDateShort(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const y = d.getFullYear()
      const m = (d.getMonth() + 1).toString().padStart(2, '0')
      const day = d.getDate().toString().padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleString()
    },
    truncateText(text, length) {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    }
  }
}
</script>

<style scoped>
.diary-page {
  max-width: 1120px;
  margin: 0 auto;
  padding: 48px 24px 64px;
}

.page-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
}

.page-title {
  font-family: var(--font-display);
  font-size: 1.75rem;
  font-weight: 800;
  color: var(--ink);
  margin: 0;
  letter-spacing: -0.03em;
}

.btn-primary {
  background: var(--accent);
  color: #fff;
  border: none;
  padding: 12px 24px;
  border-radius: var(--radius);
  font-weight: 600;
  font-size: 0.95rem;
  transition: background 0.2s;
}

.btn-primary:hover {
  background: var(--accent-hover);
}

.state-msg {
  text-align: center;
  padding: 48px 20px;
  color: var(--ink-muted);
}

.state-msg.empty {
  padding: 64px 20px;
  font-size: 1.05rem;
}

.diary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  padding-bottom: 16px;
}

.pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 16px 0 24px;
  border-top: 1px solid var(--border);
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface-card);
  color: var(--ink);
  cursor: pointer;
  font-size: 0.9rem;
}

.pagination-btn:hover:not(:disabled) {
  border-color: var(--accent);
  color: var(--accent);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 0.9rem;
  color: var(--ink-muted);
}

.diary-card {
  background: var(--surface-card);
  padding: 22px 24px;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  animation: fadeIn 0.3s ease-out forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.diary-card:hover {
  border-color: var(--border-strong);
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.diary-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
}

.diary-title {
  margin: 0;
  font-size: 1.2rem;
  color: var(--text-color);
  font-weight: 700;
}

.diary-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.85rem;
  flex-wrap: wrap;
}

.mood-tag, .weather-tag {
  background: var(--bg-color);
  padding: 4px 10px;
  border-radius: var(--radius-sm);
  color: var(--text-secondary);
  font-weight: 500;
}

.time {
  color: var(--text-muted);
}

.date-tag {
  color: var(--text-muted);
}

.diary-preview {
  color: var(--text-secondary);
  line-height: 1.65;
  margin: 0;
  font-size: 0.95rem;
}

.card-actions {
  margin-top: 15px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.2s;
}

.diary-card:hover .card-actions {
  opacity: 1;
}

.btn-icon {
  background: white;
  border: 1px solid #eee;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  box-shadow: var(--shadow-sm);
}

.btn-icon:hover {
  transform: scale(1.1);
  background: var(--bg-color);
}

.btn-icon.delete:hover {
  color: var(--danger-color);
  background: #fff0f0;
}

.modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  backdrop-filter: blur(6px);
  z-index: 1100;
  display: flex;
  justify-content: center;
  align-items: center;
}

.full-screen-modal .modal-content {
  width: 100%;
  height: 100%;
  background: var(--bg-color);
  display: flex;
  flex-direction: column;
}

.diary-modal {
  background: var(--bg-color);
}

.modal-header {
  padding: 16px 24px;
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: var(--surface-card);
  box-shadow: var(--shadow);
}

.header-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.header-title-row {
  width: 100%;
}

.header-left, .header-right {
  display: flex;
  align-items: center;
  gap: 15px;
}

.modal-body {
  padding: 0;
  flex: 1;
  overflow-y: auto;
}

.diary-body {
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
  padding: 40px 20px;
}

.title-input {
  font-size: 1.5rem;
  border: none;
  border-bottom: 1px solid transparent;
  outline: none;
  background: transparent;
  width: 100%;
  font-weight: 700;
  color: var(--text-color);
  padding: 4px 0;
  transition: border-color 0.2s;
}

.title-input:focus {
  border-bottom-color: var(--accent);
}

.content-editor {
  width: 100%;
  height: 70vh;
  border: none;
  outline: none;
  resize: none;
  font-size: 1.05rem;
  line-height: 1.8;
  color: var(--text-color);
  background: transparent;
  font-family: inherit;
  padding: 20px;
  background-image: linear-gradient(transparent 96%, var(--accent-color) 96%);
  background-size: 100% 2rem;
  line-height: 2rem;
}

.select-input {
  padding: 8px 12px;
  border: 2px solid #eee;
  border-radius: var(--border-radius-md);
  outline: none;
  background: var(--bg-color);
  color: var(--text-color);
  font-weight: 500;
}

.btn-save {
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 8px 24px;
  border-radius: var(--border-radius-round);
  cursor: pointer;
  font-weight: 600;
  box-shadow: var(--shadow-sm);
  transition: all 0.3s;
}

.btn-save:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.btn-edit {
  background: var(--secondary-color);
  color: white;
  border: none;
  padding: 8px 24px;
  border-radius: var(--border-radius-round);
  cursor: pointer;
  font-weight: 600;
  box-shadow: var(--shadow-sm);
}

.btn-back {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
}

.diary-view-content {
  font-size: 1.2rem;
  line-height: 2rem;
  color: var(--text-color);
  padding: 20px;
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  min-height: 60vh;
}

.view-meta {
  color: #999;
  font-size: 0.9rem;
  margin-bottom: 30px;
  border-bottom: 1px dashed var(--accent-color);
  padding-bottom: 15px;
}

.view-text {
  white-space: pre-wrap;
}

.empty-state {
  text-align: center;
  padding: 50px 0;
  color: #999;
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
}

@media (max-width: 768px) {
  .diary-header {
    flex-direction: column;
    gap: 15px;
  }
  
  .timeline {
    padding-left: 0;
  }
  
  .timeline::before {
    display: none;
  }
  
  .timeline-item {
    flex-direction: column;
  }
  
  .timeline-date {
    position: static;
    flex-direction: row;
    align-items: baseline;
    gap: 5px;
    margin-bottom: 10px;
    width: auto;
    text-align: left;
  }
  
  .timeline-content {
    margin-left: 0;
  }
  
  .diary-card::before {
    display: none;
  }
  
  .title-input {
    width: 100%;
    font-size: 1.4rem;
  }
  
  .content-editor {
    background-image: none;
    line-height: 1.6;
  }
  
  .header-right {
    gap: 8px;
  }
  
  .select-input {
    padding: 4px 8px;
    font-size: 12px;
  }
}
</style>
